import { useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import type { HealthApp, HealthUptimeHistory } from '@traceio-org/platform-contracts';
import { AnalyticsCard } from '@traceio-org/ui';
import { PeriodMenu } from './MenuSelect';
import {
  BUCKET_LABELS,
  UPTIME_BUCKETS,
  UPTIME_PERIODS,
  UPTIME_TARGET_PERCENT,
  cellSummary,
  formatDay,
  formatUptime,
  monthLabels,
  periodDays,
  uptimeRows,
  type UptimeCell,
  type UptimePeriod,
} from '../uptime';

interface Tip {
  row: number;
  column: number;
  left: number;
  top: number;
  align: 'start' | 'center' | 'end';
}

/** A cell's words for assistive technology: the day, its bucket in words, the figures and what happened. */
function cellLabel(cell: UptimeCell): string {
  const parts = [`${formatDay(cell.day)}${cell.today ? ' (today)' : ''}`, BUCKET_LABELS[cell.bucket]];
  if (cell.percent !== null) parts.push(cellSummary(cell).replaceAll(' · ', ', '));
  if (cell.reason) parts.push(cell.reason);
  return parts.join('. ');
}

/**
 * The uptime grid (GitHub's contribution graph, turned into one row per app): one square per UTC day, oldest on the
 * left, today on the right with a ring, coloured by the day's sampled uptime, and the period's figure at the end.
 *
 * Keyboard: the squares are an ARIA grid with one tab stop (roving tabindex); arrows move by day and app, Home and End
 * jump to the row's first and last day. Hover or focus shows the day's card; each square also carries the same words
 * as its accessible name, so colour is never the only signal.
 */
export function UptimeGrid({
  history,
  apps,
  period,
  onPeriod,
}: {
  history: HealthUptimeHistory;
  apps: HealthApp[];
  period: UptimePeriod;
  onPeriod: (period: UptimePeriod) => void;
}) {
  const rows = useMemo(() => uptimeRows(history, apps, period), [history, apps, period]);
  const days = useMemo(() => periodDays(history.to, period), [history.to, period]);
  const months = useMemo(() => monthLabels(days), [days]);
  // The tab stop, as the row and the distance from today, so it stays on the same day across a change of period.
  const [stop, setStop] = useState({ row: 0, fromEnd: 0 });
  const [tip, setTip] = useState<Tip | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const cells = useRef(new Map<string, HTMLElement>());
  const stopRow = Math.min(stop.row, Math.max(rows.length - 1, 0));
  const stopColumn = Math.max(0, period - 1 - Math.min(stop.fromEnd, period - 1));
  const empty = history.apps.length === 0;

  const show = (row: number, column: number, element: HTMLElement) => {
    const box = wrap.current?.getBoundingClientRect();
    const rect = element.getBoundingClientRect();
    if (!box) return;
    const left = rect.left - box.left + rect.width / 2;
    // Near either edge the card hangs to the inside instead of centring, so it never leaves the card.
    const align = left < 110 ? 'start' : left > box.width - 110 ? 'end' : 'center';
    setTip({ row, column, left, top: rect.top - box.top, align });
  };

  const move = (event: KeyboardEvent<HTMLElement>, row: number, column: number) => {
    let next: { row: number; column: number };
    switch (event.key) {
      case 'ArrowLeft':
        next = { row, column: Math.max(0, column - 1) };
        break;
      case 'ArrowRight':
        next = { row, column: Math.min(period - 1, column + 1) };
        break;
      case 'ArrowUp':
        next = { row: Math.max(0, row - 1), column };
        break;
      case 'ArrowDown':
        next = { row: Math.min(rows.length - 1, row + 1), column };
        break;
      case 'Home':
        next = { row, column: 0 };
        break;
      case 'End':
        next = { row, column: period - 1 };
        break;
      case 'Escape':
        setTip(null);
        return;
      default:
        return;
    }
    event.preventDefault();
    setStop({ row: next.row, fromEnd: period - 1 - next.column });
    cells.current.get(`${next.row}:${next.column}`)?.focus();
  };

  const tipCell = tip ? rows[tip.row]?.cells[tip.column] : undefined;
  const columns = { '--uptime-days': period } as CSSProperties;

  return (
    <AnalyticsCard
      title="Uptime"
      action={
        <PeriodMenu
          label="Uptime period"
          value={period}
          options={UPTIME_PERIODS.map((value) => ({ value, label: `Last ${value} days` }))}
          onChange={(value) => {
            onPeriod(value);
            setTip(null);
          }}
        />
      }
    >
      {empty ? (
        <p className="admin-muted admin-uptime-empty">
          Uptime history starts when the first sample is recorded
        </p>
      ) : null}
      <div className="admin-uptime" ref={wrap} style={columns} onMouseLeave={() => setTip(null)}>
        <div className="admin-uptime__row admin-uptime__months" aria-hidden="true">
          <span />
          <span className="admin-uptime__days">
            {months.map((month) => (
              <span key={month.column} style={{ gridColumn: `${month.column + 1} / span 6` }}>
                {month.label}
              </span>
            ))}
          </span>
          <span />
        </div>
        <div role="grid" aria-label={`Uptime by UTC day, last ${period} days`} className="admin-uptime__grid">
          {rows.map((row, rowIndex) => {
            const percent = row.percent === null ? null : formatUptime(row.percent);
            const below = row.percent !== null && row.percent < UPTIME_TARGET_PERCENT;
            return (
              <div
                key={row.appId}
                role="row"
                className="admin-uptime__row"
                aria-label={`${row.label}, ${percent ?? 'no data'} over ${period} days`}
              >
                <span role="rowheader" className="admin-uptime__name" title={row.label}>
                  {row.label}
                </span>
                <span className="admin-uptime__days">
                  {row.cells.map((cell, column) => (
                    <span
                      key={cell.day}
                      role="gridcell"
                      ref={(element) => {
                        if (element) cells.current.set(`${rowIndex}:${column}`, element);
                        else cells.current.delete(`${rowIndex}:${column}`);
                      }}
                      className="admin-uptime__day"
                      data-bucket={cell.bucket}
                      data-today={cell.today || undefined}
                      tabIndex={rowIndex === stopRow && column === stopColumn ? 0 : -1}
                      aria-label={cellLabel(cell)}
                      onMouseEnter={(event) => show(rowIndex, column, event.currentTarget)}
                      onFocus={(event) => {
                        setStop({ row: rowIndex, fromEnd: period - 1 - column });
                        show(rowIndex, column, event.currentTarget);
                      }}
                      onBlur={() => setTip(null)}
                      onKeyDown={(event) => move(event, rowIndex, column)}
                    />
                  ))}
                </span>
                <span
                  role="gridcell"
                  className="admin-uptime__percent"
                  data-below={below || undefined}
                  aria-label={
                    percent === null ? `No data over ${period} days` : `${percent} over ${period} days`
                  }
                >
                  {percent ?? <span className="admin-muted">No data</span>}
                </span>
              </div>
            );
          })}
        </div>
        {tip && tipCell ? (
          // Visual only: the focused square's accessible name already says the same.
          <div
            className="admin-uptime__tip"
            data-align={tip.align}
            style={{ left: tip.left, top: tip.top }}
            aria-hidden="true"
          >
            {/* Days are UTC days (the samples are counted per UTC day, like every Admin date), so the card says so:
                in the evening west of UTC, today's square already carries tomorrow's date. */}
            <strong>{formatDay(tipCell.day)} UTC</strong>
            <span className="admin-uptime__tip-line">
              <span className="admin-uptime__dot" data-bucket={tipCell.bucket} />
              {cellSummary(tipCell)}
            </span>
            {tipCell.reason ? <span className="admin-uptime__tip-reason">{tipCell.reason}</span> : null}
          </div>
        ) : null}
      </div>
      <ul className="admin-uptime__legend" aria-label="Legend">
        {UPTIME_BUCKETS.map((bucket) => (
          <li key={bucket}>
            <span className="admin-uptime__dot" data-bucket={bucket} aria-hidden="true" />
            {BUCKET_LABELS[bucket]}
          </li>
        ))}
      </ul>
    </AnalyticsCard>
  );
}
